import type { AssetId } from '../assets/AssetTypes';

/**
 * Animation states of the shared runner animation set BAURUN_RUNNER_V1
 * (docs/asset-specs/CHARACTER_RIG_STANDARD.md). Gameplay only ever asks for a
 * state – every character maps the states to its own clips.
 */
export const CharacterClip = {
  Idle: 'Idle',
  Run: 'Run',
  RunFast: 'RunFast',
  LaneLeft: 'LaneLeft',
  LaneRight: 'LaneRight',
  Jump: 'Jump',
  DoubleJump: 'DoubleJump',
  Fall: 'Fall',
  Land: 'Land',
  Slide: 'Slide',
  StumbleLeft: 'StumbleLeft',
  StumbleRight: 'StumbleRight',
  Hit: 'Hit',
  Caught: 'Caught',
  GameOver: 'GameOver',
  Collect: 'Collect',
  Celebrate: 'Celebrate',
  /** Extra life: stop, drink a beer, "Prost!" (the world waits – DrinkBreak). */
  Drink: 'Drink',
  /** A jump did not make it (hit while airborne): falls forward onto hands and knees, gets up. Hit = ran into it. */
  HitTrip: 'HitTrip',
  /**
   * Riding the dozer (SEG_DOZER_60M): seated on VEH_DOZER_A_SOCKET_SEAT – the rig root on the seat cushion, hands
   * on the two levers, feet on the platform, a gentle engine bob (loop). Missing in a model: Idle, standing on the
   * platform in front of the seat (DozerConfig.presentation.rider.fallbackOffset).
   */
  Drive: 'Drive',
} as const;
export type CharacterClip = (typeof CharacterClip)[keyof typeof CharacterClip];

export const ALL_CHARACTER_CLIPS: readonly CharacterClip[] = Object.values(CharacterClip);

/** Which bones a clip drives when it is layered over the running base clip. */
export const ClipLayer = {
  /** Whole body (replaces the base clip while it plays). */
  FullBody: 'full-body',
  /** Spine, head and arms only – the legs keep running underneath. */
  UpperBody: 'upper-body',
} as const;
export type ClipLayer = (typeof ClipLayer)[keyof typeof ClipLayer];

/**
 * A playable character. The PlayerController never sees this – only the view
 * layer does, so characters are swappable without touching gameplay.
 */
export interface CharacterDefinition {
  /** Character id (selection, save data) – also the asset id of its model. */
  readonly id: string;
  readonly displayName: string;
  /** Short motto from the character sheet (selection card). */
  readonly motto: string;
  /** Visual resolved through the asset manifest (GLB or greybox). */
  readonly assetId: AssetId;
  /** Skeleton standard of the rig, null for unrigged placeholders. */
  readonly skeleton: string | null;
  readonly animationSet: string | null;
  /** Animation state → animation group name inside the model. Missing states fall back to Run/Idle. */
  readonly clips: Readonly<Partial<Record<CharacterClip, string>>>;
  /**
   * Unrigged placeholders fake lean, bob and tilt procedurally. Rigged characters get
   * these from their clips and keep only a subtle lean into lane changes.
   */
  readonly proceduralPose: boolean;
  /** Exchangeable back print (ad system), if the character has one. */
  readonly adMaterial: string | null;
  /** 2D art of the menus (public/assets/ui/), null for characters that are never offered. */
  readonly art: CharacterArt | null;
}

/** Menu pictures of a selectable character – file slots the final artwork overwrites (see public/assets/ui/README.md). */
export interface CharacterArt {
  /** Card picture, 4:5, opaque JPG – head and upper body (character cards of the start screen). */
  readonly portraitUrl: string;
  /** Standing figure, transparent PNG – loading screen (the start scene already shows both runners). */
  readonly figureUrl: string;
  /** Alt text of the pictures (German). */
  readonly alt: string;
}
